// The brand API is Rails + ActiveModelSerializers. Serialized objects carry a
// `type` discriminator and use snake_case keys; collections come back as bare
// arrays (no `{ data }` envelope). These types mirror that wire shape so the
// future swap from mock data to real fetch is a near no-op.

// Every serialized resource includes a `type` discriminator (e.g. "access_token").
export interface Resource {
  type: string
}

// A numeric metric as the brand API returns it: the raw value plus the
// server-formatted (locale-aware) display string. The Rails serializers emit
// this `value` + `formatted_value` pair so the FE never formats numbers itself.
export interface Metric {
  value: number
  formatted_value: string
}

// Money metrics additionally carry the ISO currency (e.g. "EUR").
export interface MoneyMetric extends Metric {
  currency: string
}

// The status envelope the Brand KPI engine wraps every metric in (stylink-backend
// PR #1934, DEV-1640). The payload is not wrapped in a top-level `data` node.
//   - 'ok'       real number for the window (a genuine 0 is 'ok', not 'empty')
//   - 'pending'  data source not live yet (value is null/placeholder, never a fake 0)
//   - 'empty'    scope + window resolved but matched no rows
//   - 'redacted' hidden by the caller's plan (value is a placeholder, e.g. "Creator #N")
export type MetricStatus = 'ok' | 'pending' | 'empty' | 'redacted'

// A single KPI value plus its status. Numeric metrics use the default `number`;
// the redactable creator handle uses `StatusMetric<string>`. `value` is nullable
// because a `pending` metric carries no number yet.
export interface StatusMetric<T = number> {
  value: T | null
  status: MetricStatus
  // Server-rendered, locale-aware display string for numeric metrics (money in
  // the caller's locale, counts with grouping, percents spaced) — the FE renders
  // it verbatim instead of formatting `value` (note: money `value`s are minor
  // units/cents). Null when `value` is null (pending/empty); absent on the
  // redactable text username, which renders its `value` directly.
  formatted_value?: string | null
}

export interface ApiResponse<T> {
  data: T
  message?: string
}

// Error responses follow the JSON:API-style shape rendered by ApiErrorSerializer:
//   { "errors": [{ "code", "title", "detail", "field" }] }
export interface ApiErrorItem {
  code: string
  title: string
  detail?: string
  field?: string
}

export interface ApiErrorResponse {
  errors: ApiErrorItem[]
}

export interface PaginatedResponse<T> {
  data: T[]
  meta: {
    page: number
    perPage: number
    total: number
    totalPages: number
  }
}

export interface PaginationParams {
  page?: number
  perPage?: number
  [key: string]: string | number | boolean | undefined
}

export class ApiException extends Error {
  public readonly status: number
  public readonly code?: string
  public readonly errors?: Record<string, string[]>

  constructor(message: string, status: number, code?: string, errors?: Record<string, string[]>) {
    super(message)
    this.name = 'ApiException'
    this.status = status
    this.code = code
    this.errors = errors
  }

  static isUnauthorized(error: unknown): boolean {
    return error instanceof ApiException && error.status === 401
  }

  static isForbidden(error: unknown): boolean {
    return error instanceof ApiException && error.status === 403
  }

  static isNotFound(error: unknown): boolean {
    return error instanceof ApiException && error.status === 404
  }

  static isValidationError(error: unknown): boolean {
    return error instanceof ApiException && error.status === 422
  }

  // The API's localized message when the failure is a known ApiException,
  // otherwise the passed (already translated) generic fallback.
  static messageOr(error: unknown, fallback: string): string {
    return error instanceof ApiException ? error.message : fallback
  }

  // Builds an exception from the BE's `{ errors: [...] }` envelope. The first
  // item drives message/code; items carrying a `field` are grouped into the
  // `errors` map (field -> messages) for form-level validation display.
  static fromErrorResponse(status: number, body: ApiErrorResponse | null): ApiException {
    const items = body?.errors ?? []
    const [first] = items
    const message = first?.title ?? first?.detail ?? `HTTP ${String(status)}`

    let fieldErrors: Record<string, string[]> | undefined
    for (const item of items) {
      if (!item.field) continue
      fieldErrors ??= {}
      ;(fieldErrors[item.field] ??= []).push(item.detail ?? item.title)
    }

    return new ApiException(message, status, first?.code, fieldErrors)
  }
}
